/**
 * @fileoverview Shared `/discover` view-model contracts.
 *
 * The list, map, and calendar views render the same event shape with slightly
 * different projections. `DiscoverEvent` is the single source of truth so the
 * backend, web app, and tests all agree on field names, types, and units.
 *
 * Conventions:
 * - All time fields are ISO 8601 strings with a timezone offset. The server is
 *   the source of truth for `timezone`; never compute it client-side.
 * - `distanceMeters` is in meters (not km), to keep parity with the existing
 *   spatial query types.
 * - `priceMin` / `priceMax` are in the smallest currency unit's *display* form
 *   (e.g. dollars). Discover surfaces render the values directly, so floats are
 *   acceptable here. Backend storage may use integer cents; the mapping layer
 *   (M7.2) is responsible for the conversion.
 * - `badges` is an array of well-known string literals. Keep `DiscoverBadge`
 *   typed — do not widen to `string`.
 *
 * @author LOCI Web Team
 * @since WEB-46 (Discover · Tri-View Template — M1.2)
 */

import type { EventCategory } from '../common';
import type { CtaType } from '../cta';
import type { ImageUrls } from '../event';
import type { QualityStatus } from '../quality';
import type { WhyReason } from '../why-reasons';
import type { SponsoredEventMeta } from '../sponsored';

/**
 * Single entry in the category facet — `slug` is the category key an event's
 * PRIMARY assignment carries (one of the canonical ten in practice), `count`
 * is the number of events in that bucket after the same "facet against
 * neighbors" filter exclusion that powers the neighborhoods facet.
 *
 * Kept loose (`string`, not `EventCategory`) so the contract survives a future
 * category-set expansion without a coordinated deploy — consumers narrow back
 * to the typed union at the render layer.
 */
export interface DiscoverCategoryFacet {
  slug: string;
  count: number;
}

/**
 * Date-window key shared with the Discover results bar
 * (`apps/web/lib/discover/resultsCountCopy.ts` / URL `date`).
 *
 * Presets use the URL vocabulary (`tonight`, not the API keyword `today`).
 * `upcoming` is the no-`date` default. Custom calendar ranges (and API-only
 * keywords such as `this-week`) are the ISO span `YYYY-MM-DD..YYYY-MM-DD`.
 *
 * API-1746 / DES-79 — Category and Neighborhood facet counts are computed
 * over this same window. WEB-946 labels the rail from this field so copy
 * cannot invent a different scope than the API measured.
 */
export type DiscoverFacetScopeKey =
  'upcoming' | 'tonight' | 'tomorrow' | 'weekend' | 'next7' | 'next30' | `${string}..${string}`;

/**
 * Well-known badges that may appear on a `/discover` event card.
 *
 * Distinct from `BadgeType` in `./badge` (which covers operational signals like
 * `live-now`, `sold-out`, `featured`). Discover badges are editorial /
 * recommendation-driven and are owned by the recommendations + ranking layer.
 */
export type DiscoverBadge = 'top-pick' | 'editors-pick' | 'free' | 'almost-sold-out' | 'dont-sleep';

/**
 * "Artist you know" taste signal (API-1503 / WEB-447 / WEB-450). The first
 * lineup artist the connected viewer already follows — an Apple Music library /
 * frequently-played artist matched to a LOCI `Artist` row — when that artist
 * performs at the event.
 *
 * Deliberately an object `{ artistId, name }` (NOT a bare string) so the
 * discover surface shares one shape with the recommendations surface's
 * `MatchedArtist` schema and the home-rail `MatchedArtistWire`. The discover
 * card only renders `name`, but carrying `artistId` keeps the contract
 * forward-compatible with links / analytics without another wire change.
 */
export interface DiscoverMatchedArtist {
  /** LOCI `Artist` id of the matched lineup artist. */
  artistId: string;
  /** Display name of the matched artist, rendered in the card why-line. */
  name: string;
}

/**
 * PIP-87 — typed discriminator for `DiscoverEvent.venueName`. Mirrors the
 * Postgres `VenueKind` enum and the data-pipeline classifier output.
 *
 * Carrying this on the wire lets the Discover UI + category-hub top-picks
 * ranker drop unresolved rows without per-call regex on `venueName`.
 *
 * COR-124 added `unresolved-placeholder`: the label is a stand-in, not a place
 * (`TBA`, `Online`, a bare `Charlotte, NC`). Like the other unresolved values,
 * the venue name is not safe to render as a venue.
 *
 * A runtime tuple so a schema that parses the wire value (`z.enum(…)`) reads
 * the same list rather than a copy that misses the next member.
 */
export const DISCOVER_VENUE_KINDS = [
  'resolved',
  'unresolved-address',
  'unresolved-brand',
  'unresolved-placeholder',
] as const;

/** One member of {@link DISCOVER_VENUE_KINDS}. */
export type DiscoverVenueKind = (typeof DISCOVER_VENUE_KINDS)[number];

/**
 * API-1614 (spec D3) — the lifecycle statuses a **browse** surface may return.
 *
 * A deliberately narrow subset of the Postgres `EventStatus` enum: browse read
 * gates filter to `status IN ('ACTIVE','SOLD_OUT')`, so no other value can reach
 * a `DiscoverEvent`. Typing the wire field to the reachable set (rather than the
 * full enum) means a consumer's exhaustive `switch` covers exactly the cases
 * that exist, and a CANCELLED row leaking onto a browse payload is a type error
 * rather than a silent render.
 *
 * The **event detail** surface is different by design — it serves every status
 * and renders it honestly — and therefore does not use this union.
 */
export type DiscoverEventStatus = 'ACTIVE' | 'SOLD_OUT';

/**
 * PIP-87 — true when the event has a real venue label safe to surface to
 * users. Returns `true` for legacy payloads with an omitted `venueKind`
 * (default-allow) so a missing field doesn't accidentally hide healthy
 * rows during a rolling deploy.
 */
export function isResolvedVenue(event: Pick<DiscoverEvent, 'venueKind'>): boolean {
  return event.venueKind === undefined || event.venueKind === 'resolved';
}

/**
 * PIP-87 — true when the event's venue is a failed-normalisation fallback
 * (raw postal address, unresolved brand, or a COR-124 placeholder such as
 * `TBA`). Inverse of `isResolvedVenue`
 * — callers MAY drop these rows from user-facing shelves.
 */
export function isUnresolvedVenue(event: Pick<DiscoverEvent, 'venueKind'>): boolean {
  return !isResolvedVenue(event);
}

/**
 * PIP-87 — each {@link DiscoverVenueKind} as the Prisma client spells it. The
 * Postgres `VenueKind` enum stores the hyphenated form via `@map`, and Prisma
 * rewrites it to a TS-safe identifier (`unresolved_address`) client-side.
 *
 * The ONE copy of that mapping (COR-124). `satisfies` makes a kind added to
 * {@link DISCOVER_VENUE_KINDS} without its Prisma spelling a type error, where
 * the hand-copied unions and `default` branches it replaced would compile and
 * quietly read the new kind as `'resolved'`.
 */
export const VENUE_KIND_PRISMA_IDENTIFIERS = {
  resolved: 'resolved',
  'unresolved-address': 'unresolved_address',
  'unresolved-brand': 'unresolved_brand',
  'unresolved-placeholder': 'unresolved_placeholder',
} as const satisfies Record<DiscoverVenueKind, string>;

/** A `VenueKind` as the Prisma client spells it. */
export type VenueKindPrismaIdentifier = (typeof VENUE_KIND_PRISMA_IDENTIFIERS)[DiscoverVenueKind];

/**
 * PIP-87 — normalise the Prisma client's underscored TS identifier
 * (`unresolved_address`) back into the hyphenated wire-format
 * (`unresolved-address`). Anywhere the backend reads `Location.kind` and
 * serialises it onto the wire must pass through this helper. A value outside
 * the map (untyped input) falls back to `'resolved'`, as it always has.
 */
export function venueKindFromPrismaIdentifier(
  prismaValue: VenueKindPrismaIdentifier,
): DiscoverVenueKind {
  return (
    DISCOVER_VENUE_KINDS.find((kind) => VENUE_KIND_PRISMA_IDENTIFIERS[kind] === prismaValue) ??
    'resolved'
  );
}

/**
 * Canonical event shape rendered by the `/discover` list, map, and calendar
 * views. Mapping from the underlying Prisma row → `DiscoverEvent` is owned by
 * an M7 issue (out of scope here).
 */
export interface DiscoverEvent {
  /** Stable internal identifier for the event. */
  id: string;
  /** URL-safe slug for the event detail page. */
  slug: string;
  /** Display title rendered in the card headline. */
  title: string;
  /** ISO 8601 start time with timezone offset, e.g. `2026-05-20T19:00:00-04:00`. */
  startsAt: string;
  /**
   * ISO 8601 doors-open time with timezone offset, or `null` when unpublished
   * (API-1668 / DPL-19). Always emitted. Renderers MUST NOT coerce `null` to
   * {@link startsAt}; omit the doors line when this is `null`.
   *
   * Do not add a companion `doorsKnown` flag — nullability already carries
   * the signal.
   */
  doors: string | null;
  /** ISO 8601 end time with timezone offset. Optional — many events omit this. */
  endsAt?: string;
  /** IANA timezone identifier, e.g. `America/New_York`. Server-authoritative. */
  timezone: string;
  /** Neighborhood label rendered in the card meta row, e.g. `NoDa`. */
  neighborhood: string;
  /** Venue display name. */
  venueName: string;
  /**
   * Typed discriminator for `venueName` (PIP-87). Distinguishes a real
   * venue label from a failed-normalisation fallback so the UI can drop
   * unresolved rows without per-call regex on `venueName`.
   *
   * See `DiscoverVenueKind` for the union and `isResolvedVenue` /
   * `isUnresolvedVenue` helpers for ergonomic filtering. Optional on the
   * wire so legacy payloads without the field still type-check; consumers
   * default to `'resolved'` when absent.
   */
  venueKind?: DiscoverVenueKind;
  /** Single-line venue street address. */
  venueAddress: string;
  /** Venue latitude, decimal degrees, `[-90, 90]`. */
  lat: number;
  /** Venue longitude, decimal degrees, `[-180, 180]`. */
  lng: number;
  /** Distance from the user in meters. Omitted when no user location is known. */
  distanceMeters?: number;
  /**
   * Lowest published ticket price in `currency` units (e.g. dollars), or `null`
   * when the upstream feed published no price.
   *
   * **`null` is the honest unknown, and it is common** — 905 of the 935
   * in-market upcoming rows had a NULL price at the API-1614 audit. Before that
   * issue this field fabricated `0` from a NULL column while `isFree` stayed
   * `false`, producing a self-contradictory payload ("costs $0 but isn't free")
   * that every consumer had to special-case. Emitted as a number ONLY when
   * {@link priceKnown} is `true`; renderers must treat `null` as "price not
   * published" and MUST NOT coalesce it back to `0`.
   */
  priceMin: number | null;
  /**
   * Highest published ticket price in `currency` units, or `null` when unknown.
   * Equals `priceMin` for single-tier events. Always `null` exactly when
   * {@link priceMin} is `null` — the two move together.
   */
  priceMax: number | null;
  /**
   * How many distinct price tiers the upstream source declared (API-1658 /
   * DPL-24), or `null` when unknown.
   *
   * **`null` is the honest unknown.** Ticketmaster and Eventbrite rows today
   * store only a published min/max range — not a tier cardinality — so a
   * multi-price range projects `null` here rather than inventing "2 tiers"
   * from `priceMin !== priceMax`. A single published figure
   * (`priceMin === priceMax` with {@link priceKnown}) projects `1`. Never
   * coalesce `null` to `0` or `1` on a range; the UI omits the tier line when
   * this is `null`.
   */
  priceTierCount: number | null;
  /**
   * Fee disclosure for the price block (API-1658 / DPL-24).
   *
   * - `true` — the published figure excludes fees charged at checkout
   *   ("plus fees").
   * - `false` — the published figure includes fees / no additional fees.
   * - `null` — unknown. The UI MUST omit fee disclosure entirely and MUST NOT
   *   invent "no fees".
   *
   * No fee signal is persisted on `Event` today (Ticketmaster's
   * `priceRanges[].includesTaxesAndFees` is dropped at ingest), so this is
   * `null` for every current source until a schema/capture follow-up lands.
   * Fabricating `false` from a missing column is the same defect class as
   * fabricating `0` from a NULL {@link priceMin}.
   */
  plusFees: boolean | null;
  /** ISO 4217 currency code, e.g. `USD`. */
  currency: string;
  /**
   * `true` only on a real, published $0 — a genuine free signal, never an
   * absence of price data. Equivalent to `priceKnown && priceMin === 0 &&
   * priceMax === 0`; a `null` price can never produce `true`.
   */
  isFree: boolean;
  /**
   * Lifecycle status of the event (API-1614 / spec D3).
   *
   * Browse surfaces return only {@link DiscoverEventStatus} values — `ACTIVE`
   * and `SOLD_OUT`. Sold-out events used to be hidden from browse entirely by
   * an `status = 'ACTIVE'` read gate; they now surface with an honest status so
   * the UI can render a disabled CTA and a status chip instead of pretending the
   * event does not exist. CANCELLED / POSTPONED / DRAFT / COMPLETED never reach
   * a browse payload.
   *
   * Required, not optional: a consumer that silently defaults a missing status
   * to "on sale" would reintroduce exactly the dishonesty this field exists to
   * remove.
   */
  status: DiscoverEventStatus;
  /**
   * `true` when the upstream feed supplied a real start time. `false` when the
   * ingestor only had a calendar date (and `startsAt` falls on midnight in the
   * event's local day). Lets the UI render "Time TBA" instead of fabricating
   * an "8 PM" from a stripped time. Added by API-1410.
   */
  startTimeKnown: boolean;
  /**
   * `true` when the upstream feed supplied a real ticket price. `false` when
   * `priceMin` / `priceMax` are unknown and default to `0` (which would
   * otherwise render as "Free" via `isFree`). Lets the UI render "Price TBD"
   * instead of a misleading free badge. Added by API-1410.
   */
  priceKnown: boolean;
  /**
   * The server-derived CTA affordance (API-1616 / spec §2.3, D6).
   *
   * Render the button from THIS, never from `ticketUrl` — deriving client-side
   * is what produced a hardcoded "Get tickets" on free, sold-out, and cancelled
   * events alike. Browse only ever carries `buy` | `rsvp` | `free-entry` |
   * `sold-out` | `resale` (PIP-343): `cancelled` is unreachable here because CANCELLED / POSTPONED /
   * OFFSALE never pass the D3 browse gate (they remain reachable on event
   * detail, which serves every status).
   *
   * Optional because an event with no price story omits it entirely rather than
   * receiving a noncommittal button (D6). Such rows are PIP-167 quarantine
   * candidates; until that rule ships, a consumer seeing `undefined` must render
   * no CTA — never a fallback label.
   */
  ctaType?: CtaType;
  /**
   * Resolved destination for {@link ctaType}, or `null` when there is no honest
   * one — a non-actionable CTA, a link the PIP-163 sweeper marked dead, or a
   * free event with no page. Render a disabled affordance, never a dead `#`.
   *
   * For Eventbrite this is the listing page, never the checkout deep link.
   */
  ctaHref?: string | null;
  /**
   * WEB-1098 — whether this row is a Ticketmaster resale (TicketsNow, `tmr`)
   * listing: the shared PIP-343 predicate `isTicketmasterResaleEvent`
   * (`@loci/validation`), the same one the CTA derivation and the event-detail
   * `resaleListing` use, so a card and its event page can never disagree.
   *
   * Carried separately from {@link ctaType} because a resale row whose CTA
   * resolved to the venue box office is a plain `buy`: the CTA alone cannot
   * say "resale". Drives the DS 1.10.0 resale marker (`.a-resale`) and price
   * demotion. Always emitted.
   */
  resaleListing: boolean;
  /** Outbound ticket URL — affiliate or partner link. */
  ticketUrl: string;
  /** Card cover image URL. */
  imageUrl: string;
  /**
   * WEB-817 — proxied PIP-235 size variants of `imageUrl`, so a card or
   * map-hover slot can pick a right-sized rendition via `srcset` instead of
   * downloading the full-size cover.
   *
   * Absent when the row has no variants (still the overwhelming majority) or
   * when none survived proxying, so consumers MUST fall back to `imageUrl`.
   * Every key is independently optional: a source that published only a
   * 1024px rendition yields `{ large }` alone — never assume all three.
   *
   * `imageUrl` is NOT a member of this set. On Eventbrite these are the
   * focal-point-cropped renditions while `imageUrl` is the uncropped
   * original, so mixing them into one `srcset` would swap crops between
   * breakpoints.
   */
  imageUrls?: ImageUrls;
  /**
   * Canonical category slugs (e.g. `['community-culture', 'music']`), primary
   * first, so `categories[0]` is the assignment the category facet counts. A
   * stored slug outside the ten is dropped rather than sent — including a
   * non-canonical primary, which leaves a secondary leading (API-1729).
   */
  categories: EventCategory[];
  /** Editorial / recommendation badges to render on the card. */
  badges: DiscoverBadge[];
  /**
   * Free-form tag array stored on `Event.tags`. Includes the canonical vibe
   * slugs (`quiet`, `loud`, `date-night`, `solo-ok`, `family`, `21-plus`,
   * `outdoor`, `free`) populated by the data-pipeline ingestors (PIP-80) as
   * well as any source-provided category tags. Optional because pre-PIP-80
   * rows may have an empty array; producers can omit the field entirely.
   *
   * The `/discover` API's `?vibe=...` filter narrows on this column, so
   * surfacing it on the view-model lets the web client render the active
   * vibe(s) as chips on the card without a second round-trip — and lets
   * smoke tests verify the backfill landed without dropping into raw SQL.
   */
  tags?: string[];
  /**
   * Quality placement status (WEB-451). Mirrors the persisted
   * `Event.qualityStatus` column. Optional on the wire and **only projected
   * when the operator-gated quality gate is enabled** (`getQualityGatePolicy().
   * gateEnabled`, env `LOCI_QUALITY_GATE_ENABLED`, default-OFF). When absent,
   * the UI renders no quality affordance — preserving today's behavior until
   * the catalog is fully scored and the operator flips the switch.
   *
   * The UI keys the "details unconfirmed" affordance off the explicit value
   * `'LOW_CONFIDENCE'`; `'PREMIUM'` / `'STANDARD'` render nothing, and
   * `'QUARANTINED'` rows are suppressed from public reads upstream so they
   * never reach the wire.
   */
  qualityStatus?: QualityStatus;
  /**
   * PIP-407 — present only when this row stands for an evergreen
   * "daily-repost" series (the same standing product listed at the same venue
   * on many distinct days). Browse lists a series once — its next occurrence —
   * and this is how many occurrences that one row stands for, counting itself
   * and every later one (always ≥ 1). Mirrors `Event.seriesOccurrences`.
   *
   * Absent for every ordinary event. No UI renders it yet.
   */
  seriesOccurrences?: number;
  /**
   * Per-event taste-alignment measure on a 0–100 scale (FRE-924). **Bounded
   * alignment, NOT a calibrated probability** — it expresses how strongly the
   * event's categories/genres overlap the authenticated viewer's weighted
   * preference profile, mapped into a deliberately conservative confidence
   * band (it never reads 100, and weak overlaps are omitted rather than
   * inflated up to a floor). Do not render it as "% of users like you" or any
   * statistical claim.
   *
   * **Authenticated-only and signal-gated.** Present ONLY when (1) the request
   * is authenticated, (2) the viewer has a real personalization signal (saved
   * category/artist preferences or recent in-app behavior — see the
   * response-level `personalized` flag), AND (3) this specific event overlaps
   * that signal. Anonymous responses, signal-less ("soaking") viewers, and
   * events with no taste overlap all **omit** the field — never `0`, never
   * `null` — so no fabricated number is ever shown. The per-event taste ring
   * (FRE-909) and the wider personalization envelope (FRE-896) build on this.
   */
  tasteMatchPct?: number;
  /**
   * Up to **3** structured, server-rendered "why this event" reasons (FRE-928)
   * — the human-readable companion to {@link tasteMatchPct}. Each carries a
   * `code` (for client icon/theme selection) and length-capped `displayText`
   * (e.g. `"Music matches your interests"`, `"Closest to you"`,
   * `"Late-night vibe"`), ordered most-relevant first.
   *
   * **Authenticated-only.** Anonymous responses omit the field entirely (never
   * `[]`, never `null`). Unlike {@link tasteMatchPct}, reasons are NOT gated on
   * a taste signal: a signed-in "soaking" viewer can still receive contextual
   * reasons (proximity / time-of-day) for an event, while taste-derived reasons
   * (category / genre) only appear once a real preference signal exists. Events
   * with no qualifying reason omit the field.
   *
   * Reasons are derived from real signals only and never templated against
   * fabricated artist/venue names (epic guardrail). The per-event why tags
   * (FRE-910) and reason chips (FRE-909 / FRE-911 / FRE-915) build on this.
   */
  whyReasons?: WhyReason[];
  /**
   * Nominal venue capacity when published on the linked `Location` (FRE-896).
   * **Authenticated-only** — anonymous responses omit the field. Omitted when
   * the venue has no published capacity or capacity is unknown/null.
   */
  capacity?: number;
  /**
   * "Artist you know" signal (API-1503 / WEB-447 / WEB-450). Present when a
   * lineup artist the connected viewer already follows performs at this event —
   * drives the discover-card "YOU FOLLOW …" why-line, mirroring the home-rail
   * treatment.
   *
   * **Authenticated-only and honest-absence.** Anonymous responses omit the
   * field entirely (never `null`); connected viewers with no matched lineup
   * artist on this event also omit it. Like the other per-event personalization
   * fields it is response-only — it does NOT affect the `buildDiscoverQueryArgs`
   * cache key.
   */
  matchedArtist?: DiscoverMatchedArtist;
  /**
   * Sponsored-listing annotation (WEB-36 / API-1361). Present only when this
   * event was injected as a paid placement by the shared server-side
   * `injectSponsoredSlots` injector — organic events never carry this field
   * (honest-absence — omitted, never `null`). Covers both the plain
   * `/discover` list and the category-filtered view (same route, `?category=`).
   */
  sponsored?: SponsoredEventMeta;
}

/**
 * `meta` envelope returned by `GET /api/v1/events/discover` (and the
 * facets-only projection, which shares the same shape). Canonical location for
 * the wire contract — both the backend response builder
 * (`services/backend-api/src/routes/versions/v1/events/discover.ts`) and
 * `@loci/api`'s RTK Query binding (`DiscoverEventsResponse.meta`) import this,
 * so the two sides of the contract cannot silently drift apart.
 *
 * NOTE: the live wire response ALSO carries `requestedWindow` / `servedWindow`
 * (API-1441; see `specs/openapi/backend-api-v1.yaml`). They are intentionally
 * NOT typed here yet — that gap pre-dates API-1671 and is out of this issue's
 * scope; fixing it would force every existing `DiscoverMeta` literal across
 * `@loci/api` and `apps/web` to grow two more required fields for no reason
 * this issue asked for. Left as a follow-up.
 */
export interface DiscoverMeta {
  /** Total events matching every active filter, across all pages. */
  total: number;
  /** Opaque cursor for the next page; `null` at the end of the list. */
  cursor: string | null;
  /** ISO 8601 timestamp the response was generated. */
  lastUpdatedAt: string;
  /**
   * FRE-924 — authenticated-only cold-start signal. `true` when the viewer has
   * a real personalization signal (saved category/artist prefs or recent
   * behavior), `false` when signed in but still "soaking" (no signal yet).
   * **Omitted entirely for anonymous responses** — read `undefined` as
   * "not a personalized context" and frame the surface accordingly (e.g. show
   * a "tell us what you're into" prompt rather than a taste summary). Pairs
   * with the per-event {@link DiscoverEvent.tasteMatchPct} and with
   * {@link tasteMatchedTotal} below.
   */
  personalized?: boolean;
  /**
   * API-1671 — corpus-wide taste-matched total: how many events in the FULL
   * filtered set — the SAME population {@link total} counts, never the
   * returned page — carry a real taste overlap for this viewer.
   *
   * Exists because the per-event {@link DiscoverEvent.tasteMatchPct} is only
   * ever visible on the events actually returned on this page. Before this
   * field, a two-part "N in range · M match your taste" header (or a declared
   * list-tail remainder computed as in-range minus matched) had no honest way
   * to get `M` — deriving it by counting `tasteMatchPct` on the returned page
   * silently mixes a page-local count with a corpus-wide one. Two numbers
   * describing populations of different sizes reading as one sentence is
   * exactly the fabricated-confidence defect this field exists to remove.
   *
   * **Null discipline** — mirrors {@link DiscoverEvent.priceMin}'s honest-null
   * contract:
   * - `null` — no taste signal was measurable for this request: an anonymous
   *   caller, OR a signed-in viewer who is still "soaking" ({@link personalized}
   *   is `undefined` or `false`). Renderers MUST treat `null` as "no taste
   *   signal available" and MUST NOT coalesce it to `0`.
   * - `number` — the honest, measured count once the viewer has a real signal
   *   (`personalized === true`), including a genuine `0` when nothing in the
   *   filtered set happens to overlap it. A real `0` and a `null` "not
   *   measured" are never interchangeable.
   *
   * **Always present** (never omitted) — unlike {@link personalized} and
   * {@link DiscoverEvent.tasteMatchPct}, which are omitted outright for
   * anonymous responses. Consumers can pattern-match on `null` directly
   * without an `in` / `hasOwnProperty` check first.
   *
   * Gated on the identical condition the per-event field uses (authenticated
   * + {@link personalized}), so the two fields' presence can never disagree —
   * an event never carries `tasteMatchPct` while this reads `null`, or vice
   * versa.
   */
  tasteMatchedTotal: number | null;
}

// FRE-896 — authenticated discover personalization envelope + projection helper.
export type {
  DiscoverAuthenticatedEnvelope,
  DiscoverEventPersonalizationProjection,
  DiscoverFollowedArtist,
  DiscoverFollowedVenue,
  DiscoverPickedForYou,
  DiscoverPickedForYouFriendsGoing,
  DiscoverSavedSearch,
  DiscoverTasteChip,
  DiscoverTasteProfile,
} from './personalization';

/**
 * Single bucket in the discover price-histogram facet.
 *
 * WEB-730 (2026-08-11): corrected to match the real wire shape. The
 * previous `{ min: number; max: number | null; count: number }` interface
 * never matched what `services/backend-api/src/services/discoverFacets.ts`
 * (`DISCOVER_PRICE_BUCKETS.map(({ bucket }) => ({ bucket, count }))`)
 * actually emits — a bug that stayed latent because the only prior consumer
 * (`SearchFiltersSheet`'s WEB-285 zero-check) only ever read `.count` and
 * was never wired to a live discover response in production. `priceMin`/
 * `priceMax` live on `DiscoverEvent` directly (per-event, currency units);
 * this facet only carries the pre-aggregated bucket label + its count.
 *
 * Fixed buckets, display-currency units (e.g. dollars), inclusive on both
 * ends except the top ("and above") bucket:
 * `'0-0' | '1-10' | '11-25' | '26-50' | '51+'`.
 */
export interface DiscoverPriceBucket {
  /** Bucket label, e.g. `'0-0'`, `'1-10'`, `'11-25'`, `'26-50'`, `'51+'`. */
  bucket: string;
  /** Number of events in this bucket. */
  count: number;
}

/**
 * The five facet dimensions the discover pipeline aggregates. Canonical here
 * so the producer (`services/backend-api/src/services/discoverFacets.ts`,
 * which re-exports this union) and every client that reads
 * {@link DiscoverFacetCounts.degraded} name the same dimensions.
 */
export type DiscoverFacetDimension =
  'category' | 'neighborhood' | 'price' | 'date' | 'vibe' | 'genre';

/**
 * One bucket in the discover **genre** facet (WEB-832).
 *
 * Carries both halves of the deliberate key/label split documented in
 * `./genres.ts`: `key` is the URL-safe slug `?genre=` transports, `label` is
 * the source's own display casing (`"Hip-Hop/Rap"`) rendered verbatim.
 * Consumers MUST render `label` and MUST NOT reconstruct one from `key` —
 * `genreFilterKey` is lossy by design.
 *
 * The producer emits **no bucket for a NULL `sourceGenre`**. Non-Ticketmaster
 * lanes are NULL by design (Eventbrite publishes no genre; UNC-Charlotte
 * publishes no source taxonomy), and an "Other" / "Unknown" bucket would
 * manufacture a category no source ever published — as well as implying a
 * filter that, if selected, would hide every event that simply has a genre.
 * Zero-count buckets are dropped too, mirroring the `vibes` facet, so a chip
 * can never offer a filter that empties the feed.
 */
export interface DiscoverGenreFacet {
  /** URL filter key — `genreFilterKey(label)`. Stable, lowercase, kebab. */
  key: string;
  /** Source-published display label, verbatim (`'Hip-Hop/Rap'`). */
  label: string;
  /** Number of events carrying this genre under the neighbouring filters. */
  count: number;
}

/**
 * Facet counts returned alongside a discover query result. Powers the badge
 * counts on the persistent filter rail.
 *
 * Keys in `categories` and `neighborhoods` mirror the values used in
 * `DiscoverEvent.categories` and `DiscoverEvent.neighborhood` so the UI can
 * look counts up directly.
 *
 * `calendar` keys are local-date `YYYY-MM-DD` strings (in the city's
 * timezone), used to render dot indicators on the calendar view.
 */
export interface DiscoverFacetCounts {
  /**
   * Date-window the Category and Neighborhood counts were computed over
   * (API-1746). Same vocabulary as the results-bar `date` param, plus
   * `upcoming` when no date filter is active.
   */
  scopeKey: DiscoverFacetScopeKey;
  /**
   * Event count per category, as an ordered array of `{slug, count}` rows.
   *
   * WIRE-SHAPE NOTE (WEB-349, WEB-264 — same bug class):
   * This field WAS previously typed as `Partial<Record<EventCategory, number>>`
   * even though the backend (`services/backend-api/src/services/discoverFacets.ts`
   * — `DiscoverFacetsWire.categories: Array<{slug, count}>`) has always emitted
   * an array. The mismatch made
   * `(facetCounts as Record<…, number>)[slug] ?? 0` always fall through to `0`
   * — every category row in the Discover filter rail rendered as `(0)`. The
   * fix mirrors WEB-264's neighborhoods coverage: align the shared type to the
   * wire AND keep a defensive `toFacetRows`-style coercer in the consumer so a
   * future regression on either side never reproduces the silent `(0)`.
   *
   * Producers MAY omit zero-count categories. Consumers should merge the
   * payload onto a canonical category order (`CATEGORY_ORDER` in
   * `apps/web/config/categories.ts`) so all six slots render in fixed slot
   * order even when the API returns an empty array.
   */
  categories: DiscoverCategoryFacet[];
  /** Event count per neighborhood label. */
  neighborhoods: Record<string, number>;
  /**
   * Price histogram buckets, ordered ascending, keyed on each event's listed
   * low price (free = `0-0`). Events with no listed price are in no bucket.
   */
  priceHistogram: DiscoverPriceBucket[];
  /**
   * WEB-1230 (ruling R6) — events matching every other active filter that have
   * no listed price: the "+N not listed" a price preset offers to include.
   * With a price preset active, `total + priceUnlisted` is the total with
   * `includeUnpriced` on.
   *
   * Optional on the client type because a cached or older backend payload
   * predates it: treat `undefined` as "not known", never as `0`.
   */
  priceUnlisted?: number;
  /** Event count per local calendar date (`YYYY-MM-DD`). */
  calendar: Record<string, number>;
  /**
   * Dimensions whose counts on THIS payload are known-unreliable (API-1679).
   *
   * The producer emits a dimension here when it served the request but could
   * not compute that dimension honestly — today the only case is the
   * category-unfiltered spatial scan behind "facet against neighbors"
   * (`resolveCanonicalEventCount`, WEB-828), which degrades rather than
   * failing the request: on a transient DB error it falls back to the
   * category-scoped candidate set, so every UNSELECTED category tallies `0`.
   *
   * Those zeros are indistinguishable on the wire from a real "no events
   * here", which is the whole reason this marker exists: a client that
   * disables zero-count rows (see `DiscoverCategoryFilter`'s
   * `disableZeroCount`) would otherwise make a definite availability claim —
   * greyed out, `NO ACTIVE EVENTS` — on a reading the server already knows is
   * unreliable. Consumers MUST NOT harden a zero into "unavailable" for a
   * listed dimension; keep the rows interactive and drop the zero-state
   * caption.
   *
   * Additive and optional: absent (not `[]`) on a healthy payload and from
   * every pre-API-1679 producer, so `degraded?.includes(...)` is the only
   * read a client needs.
   */
  degraded?: DiscoverFacetDimension[];
  /**
   * Source-published genre counts (WEB-832), ordered by `count` descending
   * then `label` ascending — by count, not by a fixed product order like
   * `vibes`, because the genre vocabulary is the source's and open-ended.
   *
   * **Optional and additive.** Absent (not `[]`) from every pre-WEB-832
   * producer, and the facet is emitted only when at least one in-scope event
   * carries a genre. `Event.sourceGenre` is written by the Ticketmaster lane
   * alone, so on a catalogue whose TM classification backfill (PIP-243) has
   * not run this is legitimately empty — which is precisely the state the
   * consumer must render as "no genre control", never as an empty filter.
   *
   * Contains no NULL/"Other"/"Unknown" bucket — see {@link DiscoverGenreFacet}.
   */
  genres?: DiscoverGenreFacet[];
}

// Canonical vibe slugs (PIP-80) — lifted here so the web app and the
// data-pipeline ingestors share one source of truth.
export {
  VIBE_ORDER,
  VALID_VIBES,
  VIBE_QUERY_PARAM,
  VIBE_LABELS,
  FREE_VIBE,
  FREE_PRICE_RANGE,
  asVibeSlug,
  type VibeSlug,
} from './vibes';

// Source-published genre filter keys (WEB-832) — the key/label mapping shared
// by the facet producer and every client that renders or links a genre.
export {
  GENRE_QUERY_PARAM,
  GENRE_FILTER_KEY_MAX_LENGTH,
  PLACEHOLDER_GENRE_FILTER_KEYS,
  genreFilterKey,
  isGenreFilterKey,
  isPlaceholderGenreKey,
} from './genres';

// COR-125 — the reserved "Greater <City>" catch-all neighborhood bucket,
// shared by the facet producer, venue browse, and every client that renders it.
export {
  ELSEWHERE_NEIGHBORHOOD_SLUG,
  formatElsewhereNeighborhoodLabel,
  isElsewhereNeighborhoodSlug,
} from './neighborhoods';
